/**
=========================================================
* Trust Cargo - public website shared pieces
=========================================================

* The palette, the section shell, the reveal-on-scroll hook and the line-art
* backgrounds. Kept apart from the sections so the sections stay readable.
*
* Deliberately no external images: everything decorative here is CSS or
* inline SVG, so the site has nothing to download, nothing to break, and
* looks the same on a slow connection in Hebron as on a fast one.
*/

import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

/** Royal gold on deep navy - the same brand colours as the dashboard. */
export const C = {
  navy: "#0B1B3A",
  navyDeep: "#060F22",
  navySoft: "#12264D",
  gold: "#C9A227",
  goldLight: "#E4C766",
  ivory: "#F7F4EE",
  white: "#FFFFFF",
  muted: "rgba(247, 244, 238, 0.72)",
  line: "rgba(201, 162, 39, 0.28)",
};

/** The gold hairline that separates and underlines things. */
export const goldRule = {
  height: 3,
  width: 64,
  borderRadius: 3,
  background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`,
};

/**
 * Fades a block in the first time it scrolls into view.
 *
 * Returns a ref and a style object rather than wrapping in a component, so
 * it can be attached to anything without adding a DOM layer.
 */
export function useReveal(delay = 0) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    // no IntersectionObserver (old browser) - just show it
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [
    ref,
    {
      opacity: shown ? 1 : 0,
      transform: shown ? "none" : "translateY(28px)",
      transition: `opacity 700ms ease ${delay}ms, transform 700ms cubic-bezier(.2,.7,.3,1) ${delay}ms`,
    },
  ];
}

/**
 * Reveal as a wrapper rather than a hook call.
 *
 * Lists here are rendered with .map(), and calling useReveal() inside the
 * callback would change the number of hooks between renders the moment a
 * list grows - which is exactly what happens when the news posts arrive.
 * A component keeps one hook per instance, so the count is always stable.
 */
export function Reveal({ delay, children, sx }) {
  const [ref, style] = useReveal(delay);

  return (
    <SuiBox ref={ref} sx={{ ...style, height: "100%", ...sx }}>
      {children}
    </SuiBox>
  );
}

Reveal.defaultProps = { delay: 0, sx: undefined };
Reveal.propTypes = {
  delay: PropTypes.number,
  children: PropTypes.node.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  sx: PropTypes.object,
};

/** A page section with a consistent max width and rhythm. */
export function Section({ id, children, dark, tint, py }) {
  return (
    <SuiBox
      id={id}
      component="section"
      sx={{
        py: py || { xs: 7, md: 11 },
        px: { xs: 2.5, md: 5 },
        backgroundColor: dark ? C.navy : tint || C.ivory,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <SuiBox sx={{ maxWidth: 1220, mx: "auto", position: "relative", zIndex: 2 }}>
        {children}
      </SuiBox>
    </SuiBox>
  );
}

Section.defaultProps = { id: undefined, dark: false, tint: undefined, py: undefined };
Section.propTypes = {
  id: PropTypes.string,
  children: PropTypes.node.isRequired,
  dark: PropTypes.bool,
  tint: PropTypes.string,
  py: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
};

/** Section heading: small gold kicker, big title, a rule, then a lead line. */
export function Heading({ title, lead, dark, center }) {
  const [ref, style] = useReveal();

  return (
    <SuiBox
      ref={ref}
      sx={{ ...style, mb: { xs: 4, md: 6 }, textAlign: center ? "center" : "start" }}
    >
      <SuiTypography
        sx={{
          fontSize: { xs: 28, md: 40 },
          fontWeight: 800,
          lineHeight: 1.25,
          color: dark ? C.white : C.navy,
        }}
      >
        {title}
      </SuiTypography>

      <SuiBox sx={{ ...goldRule, mt: 1.5, mb: lead ? 2 : 0, mx: center ? "auto" : 0 }} />

      {lead && (
        <SuiTypography
          sx={{
            fontSize: { xs: 15, md: 18 },
            color: dark ? C.muted : "rgba(11, 27, 58, 0.7)",
            maxWidth: 720,
            mx: center ? "auto" : 0,
            lineHeight: 1.9,
          }}
        >
          {lead}
        </SuiTypography>
      )}
    </SuiBox>
  );
}

Heading.defaultProps = { lead: null, dark: false, center: false };
Heading.propTypes = {
  title: PropTypes.string.isRequired,
  lead: PropTypes.string,
  dark: PropTypes.bool,
  center: PropTypes.bool,
};

/**
 * The faint container-and-route line art behind the dark sections. Drawn
 * once as an SVG data pattern so it costs nothing to repeat.
 */
export function LineArt({ opacity }) {
  return (
    <SuiBox
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        opacity: opacity || 0.5,
        pointerEvents: "none",
        background: `
          radial-gradient(900px 420px at 12% 0%, rgba(201,162,39,0.16), transparent 65%),
          radial-gradient(760px 420px at 88% 100%, rgba(23,80,150,0.35), transparent 65%)
        `,
      }}
    />
  );
}

LineArt.defaultProps = { opacity: 0.5 };
LineArt.propTypes = { opacity: PropTypes.number };

/** A gold, filled call-to-action. */
export const goldButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  border: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  fontWeight: 700,
  fontSize: 16,
  padding: "14px 30px",
  borderRadius: 12,
  color: C.navy,
  background: `linear-gradient(135deg, ${C.goldLight}, ${C.gold})`,
  boxShadow: "0 12px 30px rgba(201, 162, 39, 0.32)",
  transition: "transform 180ms ease, box-shadow 180ms ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 18px 40px rgba(201, 162, 39, 0.42)",
  },
};

/** The quieter companion to goldButton, for the second action. */
export const ghostButton = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  cursor: "pointer",
  fontFamily: "inherit",
  fontWeight: 700,
  fontSize: 16,
  padding: "13px 28px",
  borderRadius: 12,
  color: C.ivory,
  background: "rgba(255,255,255,0.06)",
  border: `1px solid ${C.line}`,
  transition: "background-color 180ms ease, border-color 180ms ease",
  "&:hover": {
    background: "rgba(255,255,255,0.12)",
    borderColor: C.gold,
  },
};
